<app-layout-toolbar
  title="Create Order"
  [crumbs]="['Orders', 'Create']"
  backButtonText="Back"
  backButtonLink="/orders">
</app-layout-toolbar>

<div class="w-full p-6 max-w-full mx-auto">
  <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
    <div class="xl:col-span-2 flex flex-col gap-6">
      <div class="card p-6">
        <h3 class="subheading-2 m-0 mb-4">Add Products</h3>
        <mat-form-field
          class="w-full"
          appearance="outline"
          subscriptSizing="dynamic">
          <mat-icon
            matPrefix
            svgIcon="mat:search"
            class="mr-2 text-secondary"></mat-icon>
          <input
            matInput
            [formControl]="searchCtrl"
            placeholder="Search products…"
            (focus)="onSearchFocus()" />
        </mat-form-field>
        @if (showSearchResults) {
          <div
            class="border border-divider rounded-lg mt-2 max-h-48 overflow-y-auto">
            @let searchResults = searchResults$ | async;
            @let searchLoading = searchLoading$ | async;
            @if (searchResults?.length) {
              @if (searchLoading) {
                <p
                  class="px-3 py-1.5 text-xs text-secondary m-0 border-b border-divider">
                  Checking for more matches…
                </p>
              }
              @for (product of searchResults; track product.id) {
                  <button
                    class="w-full text-left px-3 py-2 hover:bg-hover transition-colors flex items-center justify-between"
                    (click)="selectProduct(product)">
                    <span class="text-sm text-default"
                      >{{ product.displayName }}
                      @if (product.name) {
                        <small>{{ product.name }}</small>
                      }
                    </span>

                    @let price = product.retailPriceMinor || 0;
                    <span class="text-xs text-secondary">{{
                      price | amountToMajor | price
                    }}</span>
                  </button>
              }
            } @else if (searchLoading) {
              <p class="p-3 text-secondary text-sm m-0">Searching…</p>
            } @else {
              <p class="p-3 text-secondary text-sm m-0">No products found.</p>
            }
          </div>
        }
      </div>
      @if (lines.length > 0) {
        <div class="card overflow-hidden">
          <table
            mat-table
            [dataSource]="lines"
            [trackBy]="trackByLineId"
            class="w-full">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef class="text-secondary">
                Product
              </th>
              <td mat-cell *matCellDef="let line" class="font-medium text-sm">
                {{ line.variantName }}
              </td>
            </ng-container>
            <ng-container matColumnDef="qty">
              <th mat-header-cell *matHeaderCellDef class="text-secondary w-24">
                Qty
              </th>
              <td mat-cell *matCellDef="let line" class="w-24">
                <div class="flex items-center gap-1">
                  <button
                    mat-icon-button
                    (click)="updateQty(line, line.quantity - 1)"
                    [disabled]="line.quantity <= 1">
                    <mat-icon svgIcon="mat:remove"></mat-icon>
                  </button>
                  <span class="text-sm font-medium w-6 text-center">{{
                    line.quantity
                  }}</span>
                  <button
                    mat-icon-button
                    (click)="updateQty(line, line.quantity + 1)">
                    <mat-icon svgIcon="mat:add"></mat-icon>
                  </button>
                </div>
              </td>
            </ng-container>
            <ng-container matColumnDef="price">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="text-secondary text-right">
                Unit Price
              </th>
              <td mat-cell *matCellDef="let line" class="text-right text-sm">
                {{ line.unitPriceMinor | amountToMajor | price }}
              </td>
            </ng-container>
            <ng-container matColumnDef="total">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="text-secondary text-right">
                Total
              </th>
              <td
                mat-cell
                *matCellDef="let line"
                class="text-right text-sm font-medium">
                {{ line.lineTotalMinor | amountToMajor | price }}
              </td>
            </ng-container>
            <ng-container matColumnDef="remove">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let line" class="text-right">
                <button
                  mat-icon-button
                  (click)="removeLine(line.id)"
                  class="text-red-500"
                  matTooltip="Remove">
                  <mat-icon svgIcon="mat:close"></mat-icon>
                </button>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="lineColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: lineColumns"></tr>
          </table>
          <mat-divider></mat-divider>
          <div class="flex justify-end p-4">
            <span class="text-sm text-secondary mr-2">Subtotal:</span>
            <span class="text-sm font-bold text-default">{{
              subtotalMinor | amountToMajor | price
            }}</span>
          </div>
        </div>
      } @else {
        <div class="card p-6 text-center">
          <p class="body-2 text-secondary m-0">
            No products added yet. Search and add products above.
          </p>
        </div>
      }
    </div>
    <div class="flex flex-col gap-6">
      <div class="card p-6">
        <h3 class="subheading-2 m-0 mb-4">Customer</h3>
        @if (customerName) {
          <div
            class="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex items-center gap-2">
              <mat-icon svgIcon="mat:person" class="text-blue-600"></mat-icon>
              <span class="text-sm font-medium text-default">{{
                customerName
              }}</span>
            </div>
            <button
              mat-icon-button
              (click)="detachCustomer()"
              matTooltip="Remove customer">
              <mat-icon svgIcon="mat:close" class="text-red-500"></mat-icon>
            </button>
          </div>
        } @else {
          <div class="relative">
            <mat-form-field
              class="w-full"
              appearance="outline"
              subscriptSizing="dynamic">
              <mat-icon
                matPrefix
                svgIcon="mat:person_search"
                class="mr-2 text-secondary"></mat-icon>
              <input
                matInput
                [formControl]="customerSearchCtrl"
                placeholder="Search by name or phone"
                autocomplete="off"
                (focus)="onCustomerSearchFocus()" />
            </mat-form-field>
            @if (showCustomerResults) {
              <div
                class="absolute z-50 w-full bg-foreground border border-divider rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                @if (customers$ | async; as customers) {
                  @if (customers.length > 0) {
                    @for (c of customers; track c.id) {
                      <button
                        class="w-full text-left px-3 py-2 hover:bg-hover transition-colors flex items-center justify-between"
                        (click)="selectCustomer(c)">
                        <div>
                          <p class="text-sm font-medium text-default m-0">
                            {{ c.firstName }} {{ c.lastName }}
                          </p>
                          <p class="text-xs text-secondary m-0">
                            {{ c.phone }}
                          </p>
                        </div>
                        <mat-icon
                          svgIcon="mat:chevron_right"
                          class="text-secondary"></mat-icon>
                      </button>
                    }
                  } @else if (customerSearchCtrl.value) {
                    <p class="p-3 text-secondary text-sm m-0 text-center">
                      No customers found
                    </p>
                  }
                }
              </div>
            }
          </div>
        }
      </div>
      <div class="card p-6" [formGroup]="orderForm">
        <h3 class="subheading-2 m-0 mb-4">Order Details</h3>
        <div class="flex flex-col gap-4">
          @if (false) {
            <mat-form-field appearance="outline">
              <mat-label>Fulfillment</mat-label>
              <mat-select formControlName="fulfillmentMode">
                @for (opt of fulfillmentOptions; track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
          <mat-form-field appearance="outline">
            <mat-label>Internal Notes</mat-label>
            <textarea
              matInput
              formControlName="notesInternal"
              rows="2"></textarea>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Notes to Customer</mat-label>
            <textarea
              matInput
              formControlName="notesToCustomer"
              rows="2"></textarea>
          </mat-form-field>
        </div>
      </div>
      <button
        mat-flat-button
        color="primary"
        class="w-full"
        (click)="submit()"
        [disabled]="submitting || lines.length === 0">
        @if (submitting) {
          <mat-spinner diameter="16" class="inline-block mr-2"></mat-spinner>
        }
        <mat-icon
          [svgIcon]="isOffline() ? 'mat:qr_code_2' : 'mat:check_circle'"
          class="mr-1"></mat-icon>
        {{ isOffline() ? "Create QR order" : "Create Order" }}
      </button>
      @if (isOffline()) {
        <p class="text-xs text-secondary mt-2 m-0" role="status">
          You're offline: instead of a payment link, a signed QR is shown for
          the customer to scan. The order is created when they check out on
          their phone.
        </p>
      }
    </div>
  </div>
</div>
